import { useState, useEffect } from 'react';
import { Menu, X, ArrowRight, Brain, Rocket, Phone, Mail, MapPin, Globe, Shield, Cpu, Video, Box, Sparkles, FileText } from 'lucide-react';
import LeadCaptureModal from '../components/LeadCaptureModal';

export default function HomePage() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  
  // Estados do modal
  const [modalState, setModalState] = useState({
    isOpen: false,
    source: 'home',
    buttonType: 'saiba_mais'
  });

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Função para abrir o modal com configurações específicas
  const openModal = (buttonType) => {
    setModalState({
      isOpen: true,
      source: 'home',
      buttonType
    });
  };

  // Função para fechar o modal
  const closeModal = () => {
    setModalState(prev => ({ ...prev, isOpen: false }));
  };

  const sdks = [
    {
      name: "Quora",
      version: "1.0",
      description: "SDK completo para criação e gerenciamento de agentes de IA",
      features: ["Agentes Ilimitados", "Multi-Provedores", "Deploy Docker"],
      gradient: "from-blue-600 to-black",
      route: "/pages/sdks/Quora/Version_1_0",
      icon: <Cpu className="w-10 h-20" />,
      badge: ""
    },
    {
      name: "Paware Data",
      version: "1.0",
      description: "Paware Data é uma pipeline inteligente para processamento de documentos",
      features: ["Multi-formato", "LGPD Compliant", "IA & Scripts"],
      gradient: "from-emerald-600 to-teal-600",
      route: "/pages/sdks/PawareData/Version_1_0",
      icon: <FileText className="w-10 h-10" />,
      badge: ""
    },
    {
      name: "Paware Vision",
      version: "1.0",
      description: "SDK & Arquitetura para análise de vídeos de longa duração",
      features: ["GPU Suporte", "Análise de Frames", "Otimização"],
      gradient: "from-black to-blue-600",
      route: "/pages/sdks/PawareVision/Version_1_0",
      icon: <Video className="w-10 h-20" />,
      badge: "Novo"
    }
  ];

  const handleSDKClick = (route) => {
    window.location.href = route;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-blue-50">
      {/* Lead Capture Modal */}
      <LeadCaptureModal
        isOpen={modalState.isOpen}
        onClose={closeModal}
        source={modalState.source}
        buttonType={modalState.buttonType}
      />

      {/* Mobile Menu Modal */}
      {isMenuOpen && (
        <div className="fixed inset-0 z-50 md:hidden">
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setIsMenuOpen(false)} />
          <div className="fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-[90%] max-w-sm bg-white rounded-2xl shadow-2xl p-8">
            <button
              onClick={() => setIsMenuOpen(false)}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
            >
              <X className="w-6 h-6" />
            </button>
            
            <nav className="flex flex-col space-y-6 text-center">
              <a href="#inicio" onClick={() => setIsMenuOpen(false)} className="text-lg font-medium text-gray-700 hover:text-blue-600 transition-colors">Início</a>
              <a href="#sobre" onClick={() => setIsMenuOpen(false)} className="text-lg font-medium text-gray-700 hover:text-blue-600 transition-colors">Sobre</a>
              <a href="#tecnologias" onClick={() => setIsMenuOpen(false)} className="text-lg font-medium text-gray-700 hover:text-blue-600 transition-colors">Tecnologias</a>
              <a href="#contato" onClick={() => setIsMenuOpen(false)} className="text-lg font-medium text-gray-700 hover:text-blue-600 transition-colors">Contato</a>
              <button 
                onClick={() => {
                  setIsMenuOpen(false);
                  openModal('orcamento');
                }}
                className="px-6 py-3 bg-gradient-to-r from-blue-600 to-cyan-600 text-white rounded-lg font-medium hover:shadow-lg transition-all"
              >
                Solicitar Orçamento
              </button>
            </nav>
          </div>
        </div>
      )}

      {/* Header */}
      <header className={`fixed top-0 w-full z-40 transition-all duration-300 ${scrolled ? 'bg-gray-900 backdrop-blur-lg shadow-lg' : 'bg-transparent'}`}>
        <div className="container mx-auto px-6">
          <div className="flex items-center justify-between h-20">
            <h1 className="text-2xl font-bold bg-gradient-to-r from-blue-400 to-cyan-400 bg-clip-text text-transparent">
              Paware Softwares
            </h1>
            
            {/* Desktop Menu */}
            <nav className="hidden md:flex items-center space-x-8">
              <a href="#inicio" className="text-white hover:text-blue-600 transition-colors">Início</a>
              <a href="#sobre" className="text-white hover:text-blue-600 transition-colors">Sobre</a>
              <a href="#tecnologias" className="text-white hover:text-blue-600 transition-colors">Tecnologias</a>
              <a href="#contato" className="text-white hover:text-blue-600 transition-colors">Contato</a>
              <button 
                onClick={() => openModal('orcamento')}
                className="px-6 py-2 bg-gradient-to-r from-blue-600 to-cyan-600 text-white rounded-lg hover:shadow-lg transition-all duration-300 hover:scale-105"
              >
                Orçamento
              </button>
            </nav>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsMenuOpen(true)}
              className="md:hidden text-gray-700"
            >
              <Menu className="w-6 h-6" />
            </button>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section 
        id="inicio" 
        className="relative pt-32 pb-20 px-6 bg-cover bg-center bg-no-repeat"
        style={{ 
          backgroundImage: `url('https://images.unsplash.com/photo-1611416517780-eff3a13b0359?fm=jpg&q=60&w=3000&ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1yZWxhdGVkfDI1fHx8ZW58MHx8fHx8')` 
        }}
      >
        {/* Overlay escuro para melhorar legibilidade */}
        <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/60 to-black/70"></div>
        
        <div className="container p-16 mx-auto max-w-6xl relative z-10">
          <div className="text-center">
            <div className="mb-8 inline-flex items-center px-4 py-2 bg-blue-600/20 backdrop-blur-md rounded-full border border-blue-400/30">
              <Sparkles className="w-4 h-4 text-blue-300 mr-2" />
              <span className="text-sm text-blue-200 font-medium">Soluções de Software Personalizadas</span>
            </div>
            
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6">
              A Paware Softwares é uma empresa
              <span className="block mt-2 bg-gradient-to-r from-blue-400 to-cyan-400 bg-clip-text text-transparent">
                especializada em soluções de softwares
              </span>
            </h1>
            
            <p className="text-2xl text-gray-200 mb-12 max-w-4xl mx-auto leading-relaxed font-medium">
              que impulsionam o crescimento do seus negócios.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button 
                onClick={() => openModal('saiba_mais')}
                className="group px-8 py-4 bg-gradient-to-r from-blue-600 to-cyan-600 text-white rounded-lg font-semibold shadow-xl hover:shadow-2xl transition-all duration-300 hover:scale-105"
              >
                <span className="flex items-center justify-center">
                  Conhecer Nossa Abordagem
                  <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
                </span>
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="sobre" className="py-10 bg-white">
        <div className="container mx-auto px-6 max-w-5xl">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Nosso Negócio
            </h2>
            <p className="text-xl text-blue-600 font-medium">
              Nossa abordagem centrada no cliente para soluções tecnológicas avançadas
            </p>
          </div>

          <div className="prose prose-lg max-w-none text-gray-700 leading-relaxed">
            <p className="text-lg mb-8">
              A Paware Softwares é uma empresa líder em tecnologia e serviços avançados, fornecendo soluções 
              personalizadas para atender às necessidades de negócios de todos os tamanhos. Fundada por um grupo 
              de visionários que começou a esboçar ideias em um pedaço de papel, a empresa agora oferece soluções 
              inteligentes e inovadoras para uma ampla gama de clientes em todo o mundo.
            </p>
            
            <p className="text-lg mb-8">
              Com uma abordagem focada no cliente, a Paware Softwares é dedicada a ajudar seus clientes a 
              alcançar o sucesso através do uso estratégico da tecnologia. Nossa missão é transformar desafios 
              complexos em soluções elegantes e eficientes.
            </p>
            
            <div className="grid md:grid-cols-3 gap-8 mt-12 mb-12">
              <div className="text-center">
                <div className="w-20 h-20 mx-auto mb-4 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-2xl flex items-center justify-center text-white">
                  <Globe className="w-10 h-10" />
                </div>
                <h3 className="font-bold text-xl mb-2">Alcance Global</h3>
                <p className="text-base">Atendemos clientes em todo o mundo com soluções personalizadas</p>
              </div>
              <div className="text-center">
                <div className="w-20 h-20 mx-auto mb-4 bg-gradient-to-br from-purple-500 to-pink-500 rounded-2xl flex items-center justify-center text-white">
                  <Brain className="w-10 h-10" />
                </div>
                <h3 className="font-bold text-xl mb-2">Inovação Constante</h3>
                <p className="text-base">Sempre em busca das tecnologias mais avançadas do mercado</p>
              </div>
              <div className="text-center">
                <div className="w-20 h-20 mx-auto mb-4 bg-gradient-to-br from-green-500 to-emerald-500 rounded-2xl flex items-center justify-center text-white">
                  <Shield className="w-10 h-10" />
                </div>
                <h3 className="font-bold text-xl mb-2">Confiança e Segurança</h3>
                <p className="text-base">Compromisso com a proteção e sucesso dos nossos clientes</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Technologies Section */}
      <section 
        id="tecnologias" 
        className="py-20 bg-cover bg-center bg-no-repeat relative"
        style={{ 
          backgroundImage: `url('https://png.pngtree.com/background/20230618/original/pngtree-random-background-of-white-3d-boxes-picture-image_3757628.jpg')` 
        }}
      >
        {/* Overlay suave para melhorar legibilidade */}
        <div className="absolute inset-0 bg-white/85"></div>
        
        <div className="container mx-auto px-6 max-w-6xl relative z-10">
          {/* SDKs Subtitle */}
          <div className="text-center mb-12">
            <div className="inline-flex items-center px-6 py-3 bg-white rounded-full shadow-md border border-gray-100">
              <Box className="w-5 h-5 text-blue-500 mr-2" />
              <span className="text-xm text-gray-700 font-semibold">Distribuição de SDKs</span>
            </div>
          </div>

          {/* SDKs Cards */}
          <div className="flex flex-wrap justify-center gap-8 max-w-5xl mx-auto">
            {sdks.map((sdk, index) => (
              <div
                key={index}
                onClick={() => handleSDKClick(sdk.route)}
                className="group relative cursor-pointer transform transition-all duration-300 hover:-translate-y-1"
              >
                {/* Sombra suave no hover */}
                <div className="absolute inset-0 bg-gradient-to-br from-blue-100/20 to-purple-100/20 rounded-2xl blur-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                
                <div className="relative bg-white rounded-2xl p-8 shadow-md hover:shadow-xl transition-all duration-300 border border-gray-100 w-80">
                  {sdk.badge && (
                    <div className="absolute -top-3 -right-3 px-4 py-2 bg-gradient-to-r from-emerald-500 to-green-500 text-white text-xs font-bold rounded-full shadow-lg">
                      {sdk.badge}
                    </div>
                  )}
                  
                  {/* Ícone com fundo gradiente suave */}
                  <div className="relative mb-6">
                    <div className="absolute inset-0 bg-gradient-to-br from-blue-400/20 to-purple-400/20 rounded-2xl blur-xl"></div>
                    <div className={`relative w-20 h-20 bg-gradient-to-br ${sdk.gradient} rounded-2xl flex items-center justify-center text-white mx-auto shadow-lg`}>
                      {sdk.icon}
                    </div>
                  </div>
                  
                  <h3 className="text-2xl font-bold text-gray-800 mb-3 text-center">
                    {sdk.name}
                    <span className="text-base font-normal text-gray-400 ml-2">v{sdk.version}</span>
                  </h3>
                  
                  <p className="text-gray-600 mb-6 text-center text-sm leading-relaxed">
                    {sdk.description}
                  </p>
                  
                  {/* Features com design mais suave */}
                  <div className="flex flex-wrap gap-2 mb-8 justify-center">
                    {sdk.features.slice(0, 3).map((feature, idx) => (
                      <span key={idx} className="px-3 py-1.5 bg-gradient-to-r from-blue-50 to-purple-50 text-gray-700 text-xs rounded-lg border border-blue-100">
                        {feature}
                      </span>
                    ))}
                  </div>
                  
                  {/* CTA mais sutil */}
                  <div className="flex items-center justify-center text-gray-600 group-hover:text-blue-600 transition-colors duration-300">
                    <span className="font-medium">Ver Detalhes</span>
                    <ArrowRight className="w-4 h-4 ml-2 transform group-hover:translate-x-1 transition-transform" />
                  </div>
                </div>
              </div>
            ))}
            
            {/* Coming Soon Card */}
            <div className="relative transform transition-all duration-300">
              <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-2xl p-8 h-full flex flex-col items-center justify-center text-center border border-gray-200 border-dashed w-80">
                <div className="w-20 h-20 bg-gradient-to-br from-gray-200 to-gray-300 rounded-2xl flex items-center justify-center mb-6 relative">
                  <Rocket className="w-10 h-10 text-gray-500" />
                  <div className="absolute -bottom-1 -right-1 w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center">
                    <span className="text-white text-xs font-bold">+</span>
                  </div>
                </div>
                <h3 className="text-xl font-semibold text-gray-700 mb-3">Mais SDKs em Breve</h3>
                <p className="text-gray-500 text-sm leading-relaxed">
                  Novos SDKs sendo desenvolvidos para acelerar seus projetos
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-blue-600 to-cyan-600">
        <div className="container mx-auto px-6 text-center">
          <h2 className="text-4xl font-bold text-white mb-6">
            Pronto para Transformar seu Negócio?
          </h2>
          <p className="text-xl text-blue-100 mb-8 max-w-2xl mx-auto">
            Entre em contato conosco e descubra como podemos ajudar sua empresa 
            a alcançar o próximo nível com nossas soluções digitais personalizadas.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button 
              onClick={() => openModal('orcamento')}
              className="px-8 py-4 bg-white text-blue-600 rounded-lg font-semibold hover:shadow-2xl transition-all duration-300 hover:scale-105"
            >
              Solicitar Orçamento
            </button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer id="contato" className="bg-gray-900 text-gray-400 py-12">
        <div className="container mx-auto px-6">
          <div className="grid md:grid-cols-3 gap-8 max-w-4xl mx-auto">
            <div>
              <h3 className="text-2xl font-bold bg-gradient-to-r from-blue-400 to-cyan-400 bg-clip-text text-transparent mb-4">
                Paware Softwares
              </h3>
              <p className="text-sm leading-relaxed">
                Transformando ideias em soluções digitais inovadoras.
              </p>
            </div>
            
            <div>
              <h4 className="text-white font-semibold mb-4">Links Rápidos</h4>
              <ul className="space-y-2 text-sm">
                <li><a href="#sobre" className="hover:text-blue-400 transition-colors">Sobre Nós</a></li>
                <li><a href="#tecnologias" className="hover:text-blue-400 transition-colors">Tecnologias</a></li>
                <li>
                  <a 
                    href="#" 
                    onClick={(e) => {
                      e.preventDefault();
                      openModal('contato');
                    }}
                    className="hover:text-blue-400 transition-colors cursor-pointer"
                  >
                    Contato
                  </a>
                </li>
              </ul>
            </div>
            
            <div>
              <h4 className="text-white font-semibold mb-4">Contato</h4>
              <div className="space-y-3 text-sm">
                <div className="flex items-center">
                  <Mail className="w-4 h-4 mr-2 text-blue-400" />
                  <span>comercial@pawaresoftwares.com.br</span>
                </div>
                <div className="flex items-center">
                  <Phone className="w-4 h-4 mr-2 text-blue-400" />
                  <span>+55 (51) 99980-2255</span>
                </div>
                <div className="flex items-center">
                  <MapPin className="w-4 h-4 mr-2 text-blue-400" />
                  <span>Brasil - Porto Alegre - Rio Grande do Sul</span>
                </div>
              </div>
            </div>
          </div>
          
          <div className="mt-8 pt-8 border-t border-gray-800 text-center text-sm">
            <p>© 2025 Paware Softwares. Todos os direitos reservados.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}